<template>
  <RevDrawer
    :close-button-label="i18n(translations.cancel)"
    data-qa="refund-orderline-drawer"
    :name="modalName"
    :title="i18n(translations.title)"
  >
    <template #body>
      <RevInfoBlock
        v-if="apiError"
        class="mt-16"
        :icon="IconWarning"
        :title="apiError.title"
        variant="danger"
      >
        {{ apiError.detail }}
      </RevInfoBlock>

      <RevForm
        :id="FORM_ID"
        :initial-values="INITIAL_VALUES"
        :validate
        @submit="handleSubmit"
      >
        <template #default="{ values, errors }">
          <p class="body-1-bold my-16">
            {{
              i18n(translations.prompt, {
                sku: orderLine.sku,
                orderId: order.id,
              })
            }}
          </p>

          <div class="body-1-bold my-12">
            {{ i18n(translations.amount) }}
          </div>

          <div class="mb-8 flex justify-between">
            <span>{{ orderLine.sku }}</span>
            <span>
              {{
                i18n.price({
                  amount: orderLine.priceWithoutTaxes.amount,
                  currency: orderLine.priceWithoutTaxes.currency,
                })
              }}
            </span>
          </div>

          <div class="mb-8 flex justify-between">
            <span>{{ i18n(translations.shipping) }}</span>
            <span>
              +
              {{
                i18n.price({
                  amount: order.shipping?.price.amount ?? '',
                  currency: order.shipping?.price.currency ?? '',
                })
              }}
            </span>
          </div>

          <div class="mb-16 flex justify-between">
            <span>{{ i18n(translations.tax) }}</span>
            <span>
              +
              {{
                i18n.price({
                  amount: orderLine.taxes?.amount || '0',
                  currency:
                    orderLine.taxes?.currency ||
                    orderLine.priceWithoutTaxes.currency,
                })
              }}
            </span>
          </div>

          <RevInputTextarea
            id="message"
            v-model="values.message"
            data-qa="refund-message"
            :error="errors.message"
            :label="i18n(translations.message)"
            :rows="5"
          />
        </template>
      </RevForm>
    </template>

    <template #footer>
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          data-qa="confirm-refund-order"
          :form="FORM_ID"
          full-width="always"
          :loading="isLoading"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.confirm) }}
        </RevButton>

        <RevLink @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevLink>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { putMerchantOrdersCancelOrderline } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputTextarea } from '@ds/components/InputTextarea'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import type { Order, OrderLine } from '../../../../../../../../Tab.types'

import {
  FORM_ID,
  INITIAL_VALUES,
  MODAL_NAME,
} from './RefundOrderLineDrawer.constants'
import translations from './RefundOrderLineDrawer.translations'

type FormValues = {
  message: string
}

const i18n = useI18n()

const props = defineProps<{
  order: Order
  orderLine: OrderLine
  refreshOrders: () => void
}>()

const { openSuccessToast } = useTheToast()

const apiError = ref<null | HttpApiError>(null)
const isLoading = ref(false)
const modalName = computed(() => MODAL_NAME + props.orderLine.id)

const validate = makeValidate<FormValues>({
  message: required(i18n(translations.required)),
})

async function handleSubmit({ message }: FormValues) {
  try {
    isLoading.value = true
    apiError.value = null

    await $httpFetch(putMerchantOrdersCancelOrderline, {
      pathParams: {
        orderLineId: props.orderLine.id,
      },
      body: {
        reason: message,
      },
    })

    openSuccessToast({
      content: i18n(translations.successMessage, {
        orderId: props.order.id,
      }),
    })
    props.refreshOrders()
    closeModal(modalName.value)
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isLoading.value = false
  }
}

function reset() {
  apiError.value = null
}

function handleCancel() {
  reset()
  closeModal(modalName.value)
}
</script>
